/**
 * Member List Component
 * Comprehensive member management with advanced search, filtering, and CRUD operations
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { memberService } from '../../services/memberService';
import { useRBAC } from '../../hooks/useRBAC';
import { getQuickFilters } from '../../utils/filterUtils';
import ProfessionalLoader from '../common/ProfessionalLoader';
import MemberEditModal from './MemberEditModal';
import { drfErrorsToFieldMap } from '../../utils/apiResponseHandler';
import MemberDetailPopup from './MemberDetailPopup';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedStats from '../common/UnifiedStats';
import UnifiedFilters from '../common/UnifiedFilters';
import UnifiedActionPanel from '../common/UnifiedActionPanel';
import UnifiedTable from '../common/UnifiedTable';
import UnifiedEmptyState from '../common/UnifiedEmptyState';
import './MemberList.css';

const MemberList = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageMembers } = useRBAC();

  // State management
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedAction, setSelectedAction] = useState('');

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editServerFieldErrors, setEditServerFieldErrors] = useState({});
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Filter states
  const [searchParams, setSearchParams] = useState({
    search: '',
    membership_status: '',
    status: '',
    state: '',
    city: '',
    screening_format: '',
    publication_preferences: '',
    show_in_external_view: '',
    email_subscribed: '',
    created_after: '',
    created_before: '',
    ordering: '-created_at'
  });
  const [searchInput, setSearchInput] = useState('');

  // Statistics
  const [stats, setStats] = useState({
    members: {
      total: 0,
      approved: 0,
      pending: 0,
      rejected: 0,
      recent_30_days: 0
    },
    mailchimp_sync: {
      synced: 0,
      pending: 0,
      failed: 0,
      not_synced: 0
    },
    email_subscriptions: {
      subscribed: 0,
      unsubscribed: 0
    },
    invoices: {
      total: 0,
      draft: 0,
      sent: 0,
      paid: 0,
      overdue: 0,
      recent_30_days: 0
    },
    xero_sync: {
      synced: 0,
      failed: 0,
      pending: 0,
      not_synced: 0
    },
    bulk_operations: {
      total: 0,
      pending: 0,
      in_progress: 0,
      completed: 0,
      failed: 0
    }
  });

  // Load members with enhanced error handling
  const loadMembers = useCallback(async (isSearch = false) => {
    try {
      if (isSearch) {
        setIsProcessing(true);
      } else {
        setIsLoading(true);
      }

      let response;

      // Use smart search if there's a search term, otherwise use regular getMembers
      if (searchParams.search && searchParams.search.trim() !== '') {
        // Extract search term and other filters
        const { search, ...otherFilters } = searchParams;
        response = await memberService.smartMemberSearch(search, otherFilters);
      } else {
        response = await memberService.getMembers(searchParams);
      }

      if (response.success) {
        setMembers(response.data || []);

        // Update statistics if not a search
        if (!isSearch) {
          const statsResponse = await memberService.getMemberStats();
          if (statsResponse.success && statsResponse.data) {
            setStats(statsResponse.data);
          }
        }
      } else {
        showError(response.message || 'Failed to load members', 'Error');
      }
    } catch (error) {
      showError('Failed to load members', 'Error');
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  }, [searchParams, showError]);

  // Load members on component mount and when search params change
  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Sync search input when searchParams.search is changed externally (e.g. quick filter "All")
  useEffect(() => {
    setSearchInput(searchParams.search || '');
  }, [searchParams.search]);

  // Handle search parameter changes
  const handleSearchChange = (newParams) => {
    setSearchParams(prev => ({
      ...prev,
      ...newParams
    }));
  };

  // Search runs only on submit (search icon click or Enter key)
  const handleSearchInputChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  const handleSearch = useCallback(() => {
    setSearchParams(prev => ({ ...prev, search: searchInput }));
  }, [searchInput]);

  // Handle member selection
  const handleMemberSelect = (memberId, isSelected) => {
    setSelectedMembers(prev => {
      if (isSelected) {
        return [...prev, memberId];
      } else {
        return prev.filter(id => id !== memberId);
      }
    });
  };

  // Handle row click for selection
  const handleRowClick = (member) => {
    const isSelected = selectedMembers.includes(member.id);
    handleMemberSelect(member.id, !isSelected);
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedMembers(members.map(member => member.id));
    } else {
      setSelectedMembers([]);
    }
  };

  // Get selection summary
  const getSelectionSummary = () => {
    if (selectedMembers.length === 0) return null;
    if (selectedMembers.length === 1) {
      const member = members.find(m => m.id === selectedMembers[0]);
      return member ? member.organization_name : '1 member';
    }
    return `${selectedMembers.length} members`;
  };

  // Handle bulk actions
  const handleBulkAction = async () => {
    if (!selectedMembers.length || !selectedAction) return;

    setIsProcessing(true);
    try {
      let response;
      let successMessage;

      if (selectedMembers.length === 1) {
        // Single member action
        const memberId = selectedMembers[0];

        switch (selectedAction) {
          case 'view':
            // Handle view action - find the member and open detail modal
            const member = members.find(m => m.id === memberId);
            if (member) {
              setSelectedMember(member);
              setShowDetailModal(true);
              setIsProcessing(false);
              return; // Exit early for view action
            } else {
              showError('Member not found', 'Error');
              setIsProcessing(false);
              return;
            }
          case 'edit':
            // Handle edit action - find the member and open edit modal
            const memberToEdit = members.find(m => m.id === memberId);
            if (memberToEdit) {
              setSelectedMember(memberToEdit);
              setShowEditModal(true);
              setIsProcessing(false);
              return; // Exit early for edit action
            } else {
              showError('Member not found', 'Error');
              setIsProcessing(false);
              return;
            }
          case 'delete':
            response = await memberService.deleteMember(memberId);
            successMessage = 'Member deleted successfully';
            break;
          default:
            // For other actions, use bulk API but with single member
            response = await memberService.bulkMemberAction(
              selectedMembers,
              selectedAction
            );
            successMessage = `Member ${selectedAction} completed successfully`;
        }
      } else {
        // Bulk member action
        if (selectedAction === 'view' || selectedAction === 'edit') {
          showError('View and Edit actions can only be performed on a single member', 'Error');
          setIsProcessing(false);
          return;
        }


        response = await memberService.bulkMemberAction(
          selectedMembers,
          selectedAction
        );
        successMessage = `Bulk ${selectedAction} completed successfully`;
      }

      if (response.success) {
        showSuccess(response.message || successMessage, 'Success');
        setSelectedMembers([]);
        setSelectedAction('');
        loadMembers();
      } else {
        const errorMessage = selectedMembers.length === 1
          ? `Failed to ${selectedAction} member`
          : 'Bulk action failed';
        showError(response.message || errorMessage, 'Error');
      }
    } catch (error) {
      const errorMessage = selectedMembers.length === 1
        ? 'Failed to perform member action'
        : 'Bulk action failed';
      showError(errorMessage, 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle member creation

  // Handle member update
  const handleUpdateMember = async (memberId, memberData) => {
    setIsProcessing(true);
    try {
      const response = await memberService.updateMember(memberId, memberData);

      if (response.success) {
        setEditServerFieldErrors({});
        showSuccess('Member updated successfully', 'Success');
        setShowEditModal(false);
        setSelectedMember(null);
        loadMembers();
      } else {
        setEditServerFieldErrors(drfErrorsToFieldMap(response.data));
        showError(response.message || 'Failed to update member', 'Error');
      }
    } catch (error) {
      showError(error.message || 'Failed to update member', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };





  // Clear selection
  const clearSelection = () => {
    setSelectedMembers([]);
    setSelectedAction('');
  };


  if (isLoading) {
    return (
      <div className="member-list-container">
        <div className="loading-container">
          <ProfessionalLoader message="Loading members..." size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="member-list-container">
      {/* Members Management Section */}
      <div className="members-management-container">

        {/* Unified Header */}
        <UnifiedHeader
          title="Member Management"
          subtitle="Manage and organize member accounts"
          icon="👥"
          count={members.length}
          countLabel="Total Members"
          selectedCount={selectedMembers.length}
          selectedLabel="Selected"
        />

        {/* Unified Statistics */}
        <UnifiedStats
          stats={[
            { title: '👥 Total Members', value: stats.members?.total || 0 },
            { title: '✅ Approved Members', value: stats.members?.approved || 0 },
            { title: '⏳ Pending Members', value: stats.members?.pending || 0 },
            { title: '🆕 Recent (30 days)', value: stats.members?.recent_30_days || 0 },
            { title: '📧 Email Subscribed', value: stats.email_subscriptions?.subscribed || 0 },
            { title: '📊 MailChimp Synced', value: stats.mailchimp_sync?.synced || 0 }
          ]}
        />

        {/* Unified Filters */}
        <UnifiedFilters
          searchValue={searchInput}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearch}
          quickFilters={getQuickFilters('members', searchParams)}
          onQuickFilterClick={(filterKey) => {
            if (filterKey === 'all') {
              setSearchInput('');
              setSearchParams({
                search: '',
                membership_status: '',
                is_active: '',
                state: '',
                city: '',
                screening_format: '',
                publication_preferences: '',
                show_in_external_view: '',
                email_subscribed: '',
                created_after: '',
                created_before: '',
                ordering: '-created_at'
              });
            } else if (filterKey === 'approved' || filterKey === 'pending' || filterKey === 'rejected') {
              handleSearchChange({ status: searchParams.status === filterKey ? '' : filterKey });
            } else if (filterKey === 'subscribed') {
              handleSearchChange({ email_subscribed: searchParams.email_subscribed === 'true' ? '' : 'true' });
            }
          }}
        />

        {/* Unified Action Panel */}
        <UnifiedActionPanel
          selectedCount={selectedMembers.length}
          totalCount={members.length}
          selectedItemsLabel="members"
          onSelectAll={handleSelectAll}
          onClearSelection={clearSelection}
          selectedAction={selectedAction}
          onActionChange={setSelectedAction}
          onExecuteAction={handleBulkAction}
          isProcessing={isProcessing}
          actionOptions={[
            {
              value: 'view',
              label: '👁️ View Details',
              disabled: selectedMembers.length > 1
            },
            {
              value: 'edit',
              label: '✏️ Edit Member',
              disabled: selectedMembers.length > 1
            },
            {
              value: 'delete',
              label: '🗑️ Delete Members',
              disabled: false
            }
          ]}
        />

        {members.length > 0 ? (
          <UnifiedTable
            columns={[
              { key: 'member_id', header: 'ID', className: 'unified-table-select-header' },
              { key: 'organization_name', header: 'Organization' },
              { key: 'contact_person', header: 'Contact Person' },
              { key: 'email', header: 'Email' },
              { key: 'location', header: 'Location' },
              { key: 'membership', header: 'Membership' },
              { key: 'status', header: 'Status' },
              { key: 'payment', header: 'Payment' },
              { key: 'sync_status', header: 'Sync Status' }
            ]}
            data={members}
            onRowClick={handleRowClick}
            onSelectRow={handleMemberSelect}
            selectedRows={selectedMembers}
            renderCell={(column, member) => {
              switch (column.key) {
                case 'member_id':
                  return <span className="unified-member-id">{member.member_id}</span>;
                case 'organization_name':
                  return (
                    <div className="unified-organization-info">
                      <div className="unified-org-name">{member.organization_name}</div>
                    </div>
                  );
                case 'contact_person':
                  return (
                    <div className="unified-contact-info">
                      <div className="unified-contact-name">
                        {member.primary_contact_name || member.president_name || 'N/A'}
                      </div>
                      <div className="unified-contact-role">
                        {member.contact_person_display || 'Contact'}
                      </div>
                    </div>
                  );
                case 'email':
                  return (
                    <a href={`mailto:${member.primary_contact_email || member.president_email}`} className="unified-email-link">
                      {member.primary_contact_email || member.president_email || 'N/A'}
                    </a>
                  );
                case 'location':
                  return (
                    <div className="unified-location-info">
                      <div className="unified-city-state">{member.city}, {member.state}</div>
                      <div className="unified-postcode">{member.postcode}</div>
                    </div>
                  );
                case 'membership':
                  return (
                    <div className="unified-membership-info">
                      <div className="unified-membership-status">
                        <span className={`unified-membership-badge ${member.membership_status}`}>
                          {member.membership_status}
                        </span>
                      </div>
                      <div className="unified-membership-date">
                        {member.membership_start_date ? new Date(member.membership_start_date).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  );
                case 'status':
                  return (
                    <div className="unified-status-info">
                      <span className={`unified-status-badge ${member.status}`}>
                        {member.status || 'N/A'}
                      </span>
                    </div>
                  );
                case 'payment':
                  return (
                    <div className="unified-payment-info">
                      <div className="unified-payment-status">
                        <span className={`unified-payment-badge ${member.payment_status}`}>
                          {member.payment_status}
                        </span>
                      </div>
                      <div className="unified-outstanding-balance">
                        ${member.outstanding_balance || '0.00'}
                      </div>
                    </div>
                  );
                case 'sync_status':
                  const googleSheetsStatus = member.sync_status || 'never';
                  const mailchimpStatus = member.mailchimp_sync_status || 'not_synced';
                  
                  const getGoogleSheetsStatusDisplay = (status) => {
                    switch(status) {
                      case 'synced': return '✓ Synced';
                      case 'pending': return '⏳ Pending';
                      case 'error': return '✗ Error';
                      case 'never': return '○ Not Synced';
                      default: return '○ Not Synced';
                    }
                  };
                  
                  const getMailChimpStatusDisplay = (status) => {
                    switch(status) {
                      case 'synced': return '✓ Synced';
                      case 'pending': return '⏳ Pending';
                      case 'failed': return '✗ Failed';
                      case 'not_synced': return '○ Not Synced';
                      default: return '○ Not Synced';
                    }
                  };
                  
                  return (
                    <div className="unified-sync-info">
                      <div className="unified-sync-status">
                        <span className="sync-label">Google Sheets:</span>
                        <span className={`unified-sync-badge ${googleSheetsStatus}`}>
                          {getGoogleSheetsStatusDisplay(googleSheetsStatus)}
                        </span>
                      </div>
                      <div className="unified-mailchimp-sync">
                        <span className="sync-label">MailChimp:</span>
                        <span className={`unified-sync-badge ${mailchimpStatus}`}>
                          {getMailChimpStatusDisplay(mailchimpStatus)}
                        </span>
                      </div>
                    </div>
                  );
                default:
                  return member[column.key];
              }
            }}
          />
        ) : (
          <UnifiedEmptyState
            icon="👥"
            title="No members found"
            description="Try adjusting your search criteria."
          />
        )}
      </div>

      {/* Modals */}
      {showEditModal && selectedMember && (
        <MemberEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedMember(null);
            setEditServerFieldErrors({});
          }}
          onUpdateMember={handleUpdateMember}
          member={selectedMember}
          isProcessing={isProcessing}
          existingMembers={members}
          serverFieldErrors={editServerFieldErrors}
          onClearServerFieldError={(field) =>
            setEditServerFieldErrors((prev) => {
              const { [field]: _removed, ...rest } = prev;
              return rest;
            })
          }
          onClearAllServerFieldErrors={() => setEditServerFieldErrors({})}
        />
      )}

      {showDetailModal && selectedMember && (
        <MemberDetailPopup
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedMember(null);
          }}
          member={selectedMember}
          isProcessing={isProcessing}
        />
      )}

    </div>
  );
};

export default MemberList;